import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, fmtDateTime, fmtNumber, fmtError, BILLING_LABELS } from "@/lib/apiClient";
import { useAuth } from "@/lib/auth";
import { useFx } from "@/lib/fx";
import ViewInCurrency from "@/components/ViewInCurrency";
import { Download as ExportIcon, Receipt, Plus, Stack } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { downloadCsv } from "@/lib/csv";

export default function Billing() {
    const { user } = useAuth();
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [requests, setRequests] = useState([]);
    const [requestStatus, setRequestStatus] = useState("open");
    const [openRaise, setOpenRaise] = useState(false);
    const [openBulk, setOpenBulk] = useState(false);
    const { base: fxBase, toBase } = useFx();

    const isAccounts = user?.role === "accounts" || user?.role === "super_admin";
    const canRaise = user?.role === "client_servicing" || user?.role === "super_admin";

    const loadRequests = async (status = requestStatus) => {
        try {
            const { data } = await api.get(`/invoice-requests${status === "all" ? "" : `?status=${status}`}`);
            setRequests(data);
        } catch (e) {
            /* swallow */
        }
    };

    useEffect(() => {
        if (isAccounts || user?.role === "client_servicing") loadRequests(requestStatus);
    }, [user?.role, requestStatus]);

    useEffect(() => {
        (async () => {
            try {
                const [a, s] = await Promise.all([
                    api.get("/billing"),
                    api.get("/billing/summary"),
                ]);
                setData(a.data);
                setSummary(s.data);
            } catch (e) {
                /* best-effort */
            }
            finally { setLoading(false); }
        })();
    }, []);

    const totals = (summary?.by_stage || []).reduce((acc, x) => {
        acc[x._id] = x;
        return acc;
    }, {});

    const exportCsv = () => {
        const stamp = new Date().toISOString().slice(0, 10);
        downloadCsv(
            `pulse-billing-${stamp}.csv`,
            [
                { key: "name", label: "Campaign" },
                { key: "client_org_name", label: "Client" },
                { key: "stage", label: "Campaign stage" },
                { key: "end_date", label: "End date" },
                { key: "revenue", label: "Revenue" },
                { key: "payout", label: "Payout" },
                { key: "billing_stage", label: "Billing stage", value: (r) => BILLING_LABELS[r.billing_stage] || r.billing_stage },
                { key: "invoice_number", label: "Invoice #" },
            ],
            data,
        );
    };

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="overline mb-1">// Accounts</div>
                    <h1 className="font-display text-4xl tracking-tighter">Billing & Revenue</h1>
                    <p className="text-slate-500 mt-2 text-sm">Visibility from creation day. Track campaigns to invoicing.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <ViewInCurrency testid="billing-view-in" />
                    {canRaise && (
                        <>
                            <Button
                                onClick={() => setOpenRaise(true)}
                                data-testid="billing-raise-invoice-request"
                                className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2"
                            >
                                <Plus size={14} weight="bold" /> Raise invoice request
                            </Button>
                            <Button
                                onClick={() => setOpenBulk(true)}
                                variant="outline"
                                data-testid="billing-raise-bulk-button"
                                className="rounded-none border-slate-300 gap-2"
                            >
                                <Stack size={14} weight="bold" /> Bulk raise
                            </Button>
                        </>
                    )}
                    <button
                        onClick={exportCsv}
                        disabled={data.length === 0}
                        data-testid="export-billing-csv"
                        className="bg-white border border-slate-300 text-slate-900 px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:border-slate-900 disabled:opacity-50 disabled:cursor-not-allowed self-start md:self-end"
                    >
                        <ExportIcon size={14} weight="bold" /> Export CSV
                    </button>
                </div>
            </header>

            {(isAccounts || user?.role === "client_servicing") && (
                <InvoiceRequestsSection
                    requests={requests}
                    isAccounts={isAccounts}
                    status={requestStatus}
                    onStatusChange={setRequestStatus}
                    onChanged={() => { loadRequests(); }}
                />
            )}

            {openRaise && (
                <RaiseInvoiceRequestModal
                    campaigns={data}
                    onClose={() => setOpenRaise(false)}
                    onCreated={() => { setOpenRaise(false); loadRequests("open"); setRequestStatus("open"); }}
                />
            )}

            {openBulk && (
                <BulkInvoiceRequestModal
                    campaigns={data}
                    onClose={() => setOpenBulk(false)}
                    onCreated={() => { setOpenBulk(false); loadRequests("open"); setRequestStatus("open"); }}
                />
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-200 border border-slate-200">
                {["pending", "ready_for_invoice", "invoiced", "paid"].map((s) => (
                    <div key={s} className="bg-white p-5" data-testid={`billing-stat-${s}`}>
                        <div className="overline">{BILLING_LABELS[s]}</div>
                        <div className="font-mono text-2xl mt-1">{fmtMoney(toBase(totals[s]?.total || 0, "USD").amount, fxBase)}</div>
                        <div className="text-xs text-slate-500 font-mono mt-0.5">{totals[s]?.count || 0} campaigns</div>
                    </div>
                ))}
            </div>

            {summary?.by_client?.length > 0 && (
                <section className="bg-white border border-slate-200 p-6">
                    <div className="overline mb-1">Revenue</div>
                    <h2 className="font-display text-xl mb-5">By Client</h2>
                    <div className="divide-y divide-slate-200">
                        {summary.by_client.map((c, i) => (
                            <div key={c._id} className="py-3 flex items-center justify-between" data-testid={`billing-by-client-${i}`}>
                                <div className="flex items-center gap-3">
                                    <span className="font-mono text-xs text-slate-400">{String(i + 1).padStart(2, "0")}</span>
                                    <span className="text-sm font-medium">{c.client_name}</span>
                                    <span className="font-mono text-xs text-slate-500">{c.count} campaigns</span>
                                </div>
                                <span className="font-mono text-sm">{fmtMoney(toBase(c.revenue, "USD").amount, fxBase)}</span>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            <section className="bg-white border border-slate-200 overflow-x-auto" data-testid="billing-table">
                {loading && <div className="p-6 font-mono text-xs text-slate-400">loading…</div>}
                {!loading && (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200">
                            <tr>
                                <th className="text-left px-4 py-3 overline">Campaign</th>
                                <th className="text-left px-4 py-3 overline">Client</th>
                                <th className="text-left px-4 py-3 overline">End date</th>
                                <th className="text-right px-4 py-3 overline">Revenue</th>
                                <th className="text-right px-4 py-3 overline">Payout</th>
                                <th className="text-left px-4 py-3 overline">Billing</th>
                                <th className="text-left px-4 py-3 overline">Invoice</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((c) => {
                                const cur = c.currency || "USD";
                                const rev = toBase(c.revenue || 0, cur);
                                const pay = toBase(c.payout || 0, cur);
                                return (
                                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`billing-row-${c.id}`}>
                                        <td className="px-4 py-3"><Link to={`/campaigns/${c.id}`} className="font-medium hover:underline">{c.name}</Link></td>
                                        <td className="px-4 py-3 text-slate-600">{c.client_org_name}</td>
                                        <td className="px-4 py-3 font-mono text-xs">{fmtDate(c.end_date)}</td>
                                        <td className="px-4 py-3 text-right font-mono">
                                            {fmtMoney(rev.amount, rev.currency)}
                                            {rev.native && cur !== fxBase && (
                                                <span className="ml-1 font-mono text-[10px] text-slate-400">{cur}</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono text-slate-500">{fmtMoney(pay.amount, pay.currency)}</td>
                                        <td className="px-4 py-3 font-mono text-xs uppercase">{BILLING_LABELS[c.billing_stage]}</td>
                                        <td className="px-4 py-3 font-mono text-xs">{c.invoice_number || "—"}</td>
                                    </tr>
                                );
                            })}
                            {data.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-slate-500">No data.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </section>
        </div>
    );
}

function InvoiceRequestsSection({ requests, isAccounts, status, onStatusChange, onChanged }) {
    const STATUSES = ["open", "processed", "rejected", "all"];
    return (
        <section className="bg-white border border-slate-200" data-testid="invoice-requests-section" id="invoice-requests">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <Receipt size={18} className="text-slate-500" />
                    <div>
                        <div className="overline">{isAccounts ? "Inbox" : "Your requests"}</div>
                        <h2 className="font-display text-lg">
                            {isAccounts ? "Invoice requests" : "Invoice requests you've raised"}
                        </h2>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex border border-slate-200" data-testid="invoice-status-filter">
                        {STATUSES.map((s) => (
                            <button
                                key={s}
                                onClick={() => onStatusChange(s)}
                                data-testid={`invoice-status-${s}`}
                                className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider ${
                                    status === s ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                    <span className="font-mono text-xs text-slate-500">{requests.length}</span>
                </div>
            </div>
            {requests.length === 0 ? (
                <div className="px-6 py-8 text-center text-sm text-slate-500" data-testid="invoice-requests-empty">
                    No invoice requests in this view.
                </div>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {requests.map((r) => (
                        <InvoiceRequestRowGlobal
                            key={r.id}
                            req={r}
                            isAccounts={isAccounts}
                            onChanged={onChanged}
                        />
                    ))}
                </ul>
            )}
        </section>
    );
}

function InvoiceRequestRowGlobal({ req, isAccounts, onChanged }) {
    const [open, setOpen] = useState(false);
    const [invNum, setInvNum] = useState("");
    const [resp, setResp] = useState("");
    const submit = async (decision) => {
        try {
            await api.post(`/invoice-requests/${req.id}/process`, {
                decision,
                accounts_response: resp || null,
                invoice_number: decision === "processed" ? (invNum || null) : null,
            });
            toast.success(`Request ${decision}`);
            onChanged?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        }
    };
    const statusStyle = {
        open: "bg-amber-50 border-amber-300 text-amber-800",
        processed: "bg-green-50 border-green-300 text-green-700",
        rejected: "bg-red-50 border-red-300 text-red-700",
    }[req.status] || "bg-slate-50 border-slate-300 text-slate-700";
    return (
        <li className="px-6 py-3" data-testid={`global-invoice-req-${req.id}`}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 border ${statusStyle}`}>{req.status}</span>
                        {req.invoice_number && (
                            <span className="font-mono text-[10px] uppercase tracking-wider bg-slate-100 text-slate-700 px-2 py-0.5">
                                INV #{req.invoice_number}
                            </span>
                        )}
                    </div>
                    <Link to={`/campaigns/${req.campaign_id}`} className="font-medium text-sm hover:underline">{req.campaign_name}</Link>
                    <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                        {req.month} · {fmtMoney(req.amount, req.currency)} · {fmtNumber(req.delivered_volume)} delivered · raised by {req.requested_by_name} · {fmtDateTime(req.created_at)}
                        {req.processed_at && (
                            <span> · processed {fmtDateTime(req.processed_at)}</span>
                        )}
                    </div>
                    {req.notes && <p className="text-xs text-slate-600 mt-1">{req.notes}</p>}
                    {req.accounts_response && (
                        <p className="text-xs text-slate-700 mt-1">
                            <span className="overline mr-1">accounts</span>{req.accounts_response}
                        </p>
                    )}
                </div>
                {isAccounts && req.status === "open" && (
                    <Button
                        onClick={() => setOpen((s) => !s)}
                        variant="outline"
                        data-testid={`global-invoice-req-toggle-${req.id}`}
                        className="rounded-none border-slate-300 h-8 text-xs whitespace-nowrap"
                    >
                        {open ? "Cancel" : "Process"}
                    </Button>
                )}
            </div>
            {open && isAccounts && req.status === "open" && (
                <div className="mt-3 border-t border-slate-100 pt-3 space-y-2">
                    <Input value={invNum} onChange={(e) => setInvNum(e.target.value)} placeholder="Invoice #" className="rounded-none border-slate-300 font-mono" />
                    <Textarea value={resp} onChange={(e) => setResp(e.target.value)} placeholder="Response to client servicing (optional)" rows={2} className="rounded-none border-slate-300" />
                    <div className="flex justify-end gap-2">
                        <Button onClick={() => submit("rejected")} variant="outline" className="rounded-none border-slate-300">Reject</Button>
                        <Button onClick={() => submit("processed")} disabled={!invNum.trim()} className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">Mark invoiced</Button>
                    </div>
                </div>
            )}
        </li>
    );
}

function RaiseInvoiceRequestModal({ campaigns, onClose, onCreated }) {
    const today = new Date();
    const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    const [search, setSearch] = useState("");
    const [campaignId, setCampaignId] = useState("");
    const [form, setForm] = useState({
        month: defaultMonth,
        delivered_volume: "",
        amount: "",
        notes: "",
    });
    const [busy, setBusy] = useState(false);

    const filtered = (campaigns || []).filter((c) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (c.name || "").toLowerCase().includes(q) ||
            (c.client_org_name || "").toLowerCase().includes(q)
        );
    });
    const selected = campaigns.find((c) => c.id === campaignId);

    // Pre-fill defaults when a campaign is selected
    const pickCampaign = (c) => {
        setCampaignId(c.id);
        setForm((f) => ({
            ...f,
            delivered_volume: f.delivered_volume || c.volume || 0,
            amount: f.amount || c.revenue || 0,
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!campaignId) {
            toast.error("Pick a campaign to invoice for.");
            return;
        }
        if (!form.month.match(/^\d{4}-\d{2}$/)) {
            toast.error("Enter month as YYYY-MM");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/campaigns/${campaignId}/invoice-requests`, {
                month: form.month,
                delivered_volume: Number(form.delivered_volume) || 0,
                amount: Number(form.amount) || 0,
                currency: selected?.currency,
                notes: form.notes || null,
            });
            toast.success("Invoice request raised — accounts has been notified");
            onCreated?.();
        } catch (e) {
            toast.error(fmtError(e.response?.data?.detail) || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="billing-invoice-request-modal">
            <form onSubmit={submit} className="bg-white border border-slate-200 max-w-2xl w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline">Raise</div>
                        <h3 className="font-display text-lg">Invoice request</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-500" data-testid="billing-invoice-request-close">×</button>
                </div>
                <div className="p-6 space-y-5">
                    <div>
                        <Label className="overline mb-1.5 block">Campaign</Label>
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search campaigns by name or client…"
                            className="rounded-none border-slate-300 font-mono mb-2"
                            data-testid="billing-invoice-campaign-search"
                        />
                        <div className="border border-slate-200 max-h-60 overflow-y-auto scrollbar-thin" data-testid="billing-invoice-campaign-list">
                            {filtered.length === 0 ? (
                                <div className="px-3 py-4 text-xs text-slate-500 text-center">No matches.</div>
                            ) : (
                                filtered.slice(0, 50).map((c) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        onClick={() => pickCampaign(c)}
                                        data-testid={`billing-invoice-campaign-${c.id}`}
                                        className={`w-full text-left px-3 py-2 border-b border-slate-100 hover:bg-slate-50 ${
                                            campaignId === c.id ? "bg-slate-900 text-white hover:bg-slate-800" : ""
                                        }`}
                                    >
                                        <div className="text-sm font-medium truncate">{c.name}</div>
                                        <div className={`text-[10px] font-mono mt-0.5 ${campaignId === c.id ? "text-slate-300" : "text-slate-500"}`}>
                                            {c.client_org_name} · {c.stage}{c.currency ? ` · ${c.currency}` : ""}
                                            {c.billing_stage && c.billing_stage !== "pending" ? ` · ${BILLING_LABELS[c.billing_stage] || c.billing_stage}` : ""}
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                        {selected && (
                            <div className="overline mt-2 text-slate-500" style={{ fontSize: 9 }}>
                                selected: <span className="text-slate-900">{selected.name}</span> · {selected.client_org_name}
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label className="overline mb-1.5 block">Month (YYYY-MM)</Label>
                            <Input
                                required
                                value={form.month}
                                onChange={(e) => setForm({ ...form, month: e.target.value })}
                                data-testid="billing-invoice-month"
                                placeholder="2026-05"
                                className="rounded-none border-slate-300 font-mono"
                            />
                        </div>
                        <div>
                            <Label className="overline mb-1.5 block">Delivered volume</Label>
                            <Input
                                type="number"
                                value={form.delivered_volume}
                                onChange={(e) => setForm({ ...form, delivered_volume: e.target.value })}
                                data-testid="billing-invoice-volume"
                                className="rounded-none border-slate-300 font-mono"
                            />
                        </div>
                        <div>
                            <Label className="overline mb-1.5 block">Amount{selected?.currency ? ` (${selected.currency})` : ""}</Label>
                            <Input
                                type="number"
                                required
                                value={form.amount}
                                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                                data-testid="billing-invoice-amount"
                                className="rounded-none border-slate-300 font-mono"
                            />
                        </div>
                    </div>

                    <div>
                        <Label className="overline mb-1.5 block">Notes for accounts</Label>
                        <Textarea
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={3}
                            data-testid="billing-invoice-notes"
                            className="rounded-none border-slate-300"
                            placeholder="Reference any PO, contract clause, or delivery proof…"
                        />
                    </div>
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button
                        type="submit"
                        disabled={busy || !campaignId}
                        data-testid="billing-invoice-submit"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6"
                    >
                        {busy ? "Sending…" : "Send to accounts"}
                    </Button>
                </div>
            </form>
        </div>
    );
}


function BulkInvoiceRequestModal({ campaigns, onClose, onCreated }) {
    const { base: fxBase, toBase } = useFx();
    const today = new Date();
    const defaultMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
    // Persist clientFilter + month between sessions via localStorage
    const LS_PREFS = "pulse.bulk.prefs";
    const initial = (() => {
        try {
            const raw = localStorage.getItem(LS_PREFS);
            if (raw) return JSON.parse(raw) || {};
        } catch (e) {
            /* localStorage unavailable */
        }
        return {};
    })();
    const [month, setMonth] = useState(initial.month || defaultMonth);
    const [search, setSearch] = useState("");
    const [clientFilter, setClientFilter] = useState(initial.clientFilter || "__all__");
    const [selections, setSelections] = useState({}); // campaign_id -> {checked, delivered_volume, amount, currency, notes}
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);

    // Save prefs whenever they change
    useEffect(() => {
        try {
            localStorage.setItem(LS_PREFS, JSON.stringify({ month, clientFilter }));
        } catch (e) {
            /* localStorage unavailable */
        }
    }, [month, clientFilter]);

    // Derive unique client list from campaigns
    const clientOptions = React.useMemo(() => {
        const map = new Map();
        for (const c of campaigns || []) {
            if (c.client_org_id && c.client_org_name) {
                map.set(c.client_org_id, c.client_org_name);
            }
        }
        return Array.from(map.entries())
            .sort(([, a], [, b]) => a.localeCompare(b))
            .map(([id, name]) => ({ id, name }));
    }, [campaigns]);

    const filtered = (campaigns || []).filter((c) => {
        if (clientFilter !== "__all__" && c.client_org_id !== clientFilter) return false;
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (c.name || "").toLowerCase().includes(q) ||
            (c.client_org_name || "").toLowerCase().includes(q)
        );
    });

    const toggle = (c) => {
        setSelections((prev) => {
            const next = { ...prev };
            if (next[c.id]?.checked) {
                next[c.id] = { ...next[c.id], checked: false };
            } else {
                next[c.id] = {
                    checked: true,
                    delivered_volume: c.volume || 0,
                    amount: c.revenue || 0,
                    currency: c.currency || "USD",
                    notes: "",
                };
            }
            return next;
        });
    };

    const updateField = (id, patch) => {
        setSelections((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
    };

    const selectAllVisible = () => {
        const next = { ...selections };
        for (const c of filtered) {
            if (!next[c.id]?.checked) {
                next[c.id] = {
                    checked: true,
                    delivered_volume: c.volume || 0,
                    amount: c.revenue || 0,
                    currency: c.currency || "USD",
                    notes: "",
                };
            }
        }
        setSelections(next);
    };

    const clearAll = () => setSelections({});

    const selectedItems = Object.entries(selections).filter(([, v]) => v?.checked);

    // Tally totals per currency (handles mixed-currency selections)
    const totalsByCurrency = selectedItems.reduce((acc, [, v]) => {
        const cur = v.currency || "USD";
        acc[cur] = (acc[cur] || 0) + (Number(v.amount) || 0);
        return acc;
    }, {});
    const currenciesUsed = Object.keys(totalsByCurrency);

    const submit = async (e) => {
        e.preventDefault();
        if (selectedItems.length === 0) {
            toast.error("Pick at least one campaign.");
            return;
        }
        if (!month.match(/^\d{4}-(0[1-9]|1[0-2])$/)) {
            toast.error("Enter month as YYYY-MM");
            return;
        }
        setBusy(true);
        try {
            const items = selectedItems.map(([campaign_id, v]) => ({
                campaign_id,
                delivered_volume: Number(v.delivered_volume) || 0,
                amount: Number(v.amount) || 0,
                currency: v.currency,
                notes: (v.notes && v.notes.trim()) ? v.notes.trim() : null,
            }));
            const { data } = await api.post("/invoice-requests/bulk", {
                month,
                items,
                notes: notes || null,
            });
            const created = (data?.created || []).length;
            const errors = (data?.errors || []).length;
            if (errors > 0) {
                toast.warning(`${created} raised · ${errors} failed`);
            } else {
                toast.success(`${created} invoice request${created === 1 ? "" : "s"} raised`);
            }
            onCreated?.();
        } catch (err) {
            toast.error(fmtError(err.response?.data?.detail) || err.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="billing-bulk-modal">
            <form onSubmit={submit} className="bg-white border border-slate-200 max-w-4xl w-full mt-12 mb-12">
                <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="overline">Raise multiple</div>
                        <h3 className="font-display text-lg">Bulk invoice requests</h3>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-slate-500" data-testid="billing-bulk-close">×</button>
                </div>
                <div className="p-6 space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label className="overline mb-1.5 block">Month (YYYY-MM)</Label>
                            <Input
                                required
                                value={month}
                                onChange={(e) => setMonth(e.target.value)}
                                data-testid="bulk-month"
                                placeholder="2026-05"
                                className="rounded-none border-slate-300 font-mono"
                            />
                        </div>
                        <div>
                            <Label className="overline mb-1.5 block">Client filter</Label>
                            <Select value={clientFilter} onValueChange={setClientFilter}>
                                <SelectTrigger data-testid="bulk-client-filter" className="rounded-none h-10 border-slate-300">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__all__">All clients ({clientOptions.length})</SelectItem>
                                    {clientOptions.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="overline mb-1.5 block">Shared note (used when per-row note is empty)</Label>
                            <Input
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                data-testid="bulk-notes"
                                placeholder="Optional shared note…"
                                className="rounded-none border-slate-300"
                            />
                        </div>
                    </div>

                    <div>
                        <div className="flex items-end justify-between mb-2 gap-3 flex-wrap">
                            <Label className="overline">
                                Campaigns ({selectedItems.length} selected · showing {filtered.length})
                            </Label>
                            <div className="flex items-center gap-2">
                                <Input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Filter campaigns…"
                                    className="rounded-none border-slate-300 h-8 w-56 text-xs"
                                    data-testid="bulk-search"
                                />
                                <button
                                    type="button"
                                    onClick={selectAllVisible}
                                    data-testid="bulk-select-all-visible"
                                    className="text-[11px] font-mono uppercase tracking-wider text-[var(--c-accent)] hover:underline"
                                >
                                    Select visible
                                </button>
                                <span className="text-slate-300">·</span>
                                <button
                                    type="button"
                                    onClick={clearAll}
                                    data-testid="bulk-clear-all"
                                    className="text-[11px] font-mono uppercase tracking-wider text-slate-600 hover:text-slate-900"
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                        <div className="border border-slate-200 max-h-[400px] overflow-y-auto scrollbar-thin" data-testid="bulk-campaign-list">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 sticky top-0">
                                    <tr>
                                        <th className="w-8 px-3 py-2"></th>
                                        <th className="text-left px-3 py-2 overline" style={{ fontSize: 9 }}>Campaign</th>
                                        <th className="text-right px-3 py-2 overline" style={{ fontSize: 9 }}>Volume</th>
                                        <th className="text-right px-3 py-2 overline" style={{ fontSize: 9 }}>Amount</th>
                                        <th className="text-left px-3 py-2 overline" style={{ fontSize: 9 }}>Per-row note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.slice(0, 200).map((c) => {
                                        const sel = selections[c.id];
                                        return (
                                            <tr key={c.id} className={`border-t border-slate-100 ${sel?.checked ? "bg-blue-50" : ""}`} data-testid={`bulk-row-${c.id}`}>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={!!sel?.checked}
                                                        onChange={() => toggle(c)}
                                                        className="w-4 h-4 accent-slate-900"
                                                        data-testid={`bulk-check-${c.id}`}
                                                    />
                                                </td>
                                                <td className="px-3 py-2 min-w-0">
                                                    <div className="text-sm font-medium truncate">{c.name}</div>
                                                    <div className="text-[10px] font-mono text-slate-500 truncate">
                                                        {c.client_org_name} · {c.stage}
                                                        {c.currency ? ` · ${c.currency}` : ""}
                                                        {c.billing_stage && c.billing_stage !== "pending" ? ` · ${BILLING_LABELS[c.billing_stage] || c.billing_stage}` : ""}
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Input
                                                        type="number"
                                                        value={sel?.delivered_volume ?? c.volume ?? 0}
                                                        onChange={(e) => updateField(c.id, { delivered_volume: e.target.value })}
                                                        disabled={!sel?.checked}
                                                        className="rounded-none border-slate-300 h-8 text-right font-mono w-24 ml-auto"
                                                        data-testid={`bulk-volume-${c.id}`}
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Input
                                                        type="number"
                                                        value={sel?.amount ?? c.revenue ?? 0}
                                                        onChange={(e) => updateField(c.id, { amount: e.target.value })}
                                                        disabled={!sel?.checked}
                                                        className="rounded-none border-slate-300 h-8 text-right font-mono w-24 ml-auto"
                                                        data-testid={`bulk-amount-${c.id}`}
                                                    />
                                                </td>
                                                <td className="px-3 py-2">
                                                    <Input
                                                        value={sel?.notes || ""}
                                                        onChange={(e) => updateField(c.id, { notes: e.target.value })}
                                                        disabled={!sel?.checked}
                                                        placeholder={notes ? "(shared note)" : "—"}
                                                        className="rounded-none border-slate-300 h-8 text-xs w-48"
                                                        data-testid={`bulk-notes-${c.id}`}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {filtered.length === 0 && (
                                <div className="px-3 py-6 text-xs text-slate-500 text-center">No campaigns match the filter.</div>
                            )}
                            {filtered.length > 200 && (
                                <div className="px-3 py-2 text-[10px] font-mono text-slate-500 text-center border-t border-slate-100">
                                    Showing first 200 of {filtered.length} — narrow the filter to see more.
                                </div>
                            )}
                        </div>
                    </div>

                    {selectedItems.length > 0 && (
                        <div className="bg-slate-50 border border-slate-200 px-4 py-3 flex flex-col gap-2 text-sm" data-testid="bulk-totals-footer">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <span>
                                    <b>{selectedItems.length}</b> campaigns selected
                                </span>
                                {currenciesUsed.length === 1 ? (
                                    <span className="font-mono" data-testid="bulk-total-single">
                                        total ≈ {fmtMoney(totalsByCurrency[currenciesUsed[0]], currenciesUsed[0])}
                                    </span>
                                ) : (
                                    <div className="flex flex-wrap items-center gap-3" data-testid="bulk-total-multi">
                                        <span className="overline" style={{ fontSize: 9 }}>per currency</span>
                                        {currenciesUsed.sort().map((cur) => (
                                            <span key={cur} className="font-mono text-xs" data-testid={`bulk-total-${cur}`}>
                                                {cur}: {fmtMoney(totalsByCurrency[cur], cur)}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {currenciesUsed.length > 1 && (() => {
                                // Sum FX-converted amounts into the viewing base
                                let convertedTotal = 0;
                                let anyMissing = false;
                                for (const cur of currenciesUsed) {
                                    const conv = toBase(totalsByCurrency[cur], cur);
                                    if (conv.native && cur !== fxBase) {
                                        anyMissing = true;
                                    }
                                    convertedTotal += Number(conv.amount) || 0;
                                }
                                return (
                                    <div className="flex items-center justify-between gap-3 border-t border-slate-200 pt-2 text-xs" data-testid="bulk-total-converted">
                                        <span className="overline" style={{ fontSize: 9 }}>grand total · converted</span>
                                        <span className="font-mono">
                                            ≈ {fmtMoney(convertedTotal, fxBase)}
                                            {anyMissing && (
                                                <span className="ml-2 font-mono text-[10px] text-amber-700">(some rates unavailable)</span>
                                            )}
                                        </span>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
                <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose} className="rounded-none border-slate-300">Cancel</Button>
                    <Button
                        type="submit"
                        disabled={busy || selectedItems.length === 0}
                        data-testid="bulk-submit"
                        className="rounded-none bg-slate-900 hover:bg-slate-800 text-white px-6"
                    >
                        {busy ? "Sending…" : `Raise ${selectedItems.length || ""} request${selectedItems.length === 1 ? "" : "s"}`}
                    </Button>
                </div>
            </form>
        </div>
    );
}

